import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@src/components/common/Button";
import Footer from "@src/components/common/footer";
import Input from "@src/components/common/Input";
import Label from "@src/components/common/Label";
import { RECOVER_PASSWORD, CLICK_RESET_PASSWORD } from "@src/constants/segment";
import { forgotPassword } from "@src/services/login";
import { loginActions } from "@src/store/slices/login";
import { selectLoginError, selectLoginLoading } from "@src/store/slices/login/selectors";
import { useAnalytics } from "@src/utils/analytics";
import { toast } from "@src/utils/toast";
import { EmailValidationSchema, forgotPasswordData } from "@src/utils/validationForm";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import style from "./forgotpassword.module.scss";

import { useAppDispatch, useAppSelector } from "@src/store/hooks";

export default function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<forgotPasswordData>({
    mode: "onChange",
    resolver: zodResolver(EmailValidationSchema),
  });
  const [hasLoaded, setHasLoaded] = useState(true);

  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectLoginLoading);
  const hasError = useAppSelector(selectLoginError);
  const { trackButtonClick } = useAnalytics();
  const router = useRouter();
  const plataformName = `${process.env.NEXT_PUBLIC_PLATAFORM_NAME}`;
  const pageTitle = `${plataformName} | Recuperação de Senha`;

  useEffect(() => {
    if (!hasLoaded) return;
    trackButtonClick(CLICK_RESET_PASSWORD);
    setHasLoaded(false);
  }, [hasLoaded]);

  const onSubmit = async (data: any) => {
    try {
      const response = await dispatch(forgotPassword(data?.email));
      toast({
        title: "E-mail enviado",
        message: response.message,
        type: "success",
      });
      router.push("/login");
    } catch (err) {
      toast({
        title: "Redefinição de senha não permitida",
        message: typeof err === "string" ? err : "Ocorreu um erro inesperado",
        type: "error",
      });
    }
  };

  const logoImagem = (
    <Image
      src={"/logoLogin.svg"}
      alt="logo imagem"
      width={168}
      height={72}
      style={{ margin: "0 auto" }}
      priority
    />
  );

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <div className={style.forgot__container}>
        <div className={style.forgot__container__image}>
          <div
            style={{
              display: "table",
              background: `url('/binary.png')`,
              width: "100%",
              height: "100%",
              backgroundSize: "cover",
              backgroundPosition: "30% 45%",
            }}
          ></div>
        </div>
        <div className={style.forgot__container__content}>
          <div className={style.forgot__content}>
            <div className={style.forgot__message}>
              <div>{logoImagem}</div>

              <h2>Esqueceu a senha?</h2>

              <p>
                Não se preocupe! Informe o e-mail cadastrado e enviaremos instruções para você
                recuperar o acesso.
              </p>
            </div>
            <div className={style.forgot__form}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <Label className={style.forgot__label}>Digite o e-mail cadastrado</Label>
                  <Input
                    error={Boolean(errors.email) || hasError}
                    type="email"
                    placeholder="E-mail"
                    id="email"
                    name="email"
                    {...register("email", { required: true })}
                    className="mt-1"
                  />
                </div>
                <div className={style.forgot__buttons}>
                  <Button
                    onClick={() => {
                      trackButtonClick(RECOVER_PASSWORD);
                    }}
                    isLoading={isLoading}
                    disabled={!isValid || isLoading}
                    size="medium"
                    variant="afya"
                    full
                  >
                    Recuperar senha
                  </Button>
                  <Button
                    size="medium"
                    variant="secondary"
                    full
                    type="button"
                    onClick={() => {
                      dispatch(loginActions.controlView({ hasError: false }));
                      router.push("/login");
                    }}
                  >
                    Voltar para o login
                  </Button>
                </div>
              </form>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
